import { forwardRef, MouseEvent } from 'react';
import { Link, useNavigate, type LinkProps } from 'react-router-dom';
import { useNavigationGuard } from './NavigationGuardContext';

type GuardedLinkProps = LinkProps & {
  // Vetoes the click before anything else runs — including the composed
  // `onClick` below, which is deferred until navigation actually happens and
  // must not fire at all for a disabled item.
  disabled?: boolean;
};

/**
 * Drop-in replacement for react-router-dom's `Link` that defers navigation
 * — and the composed `onClick`'s side effects — to `guardedNavigate`, so
 * nothing fires until navigation actually happens (immediately if unblocked,
 * or once the user confirms the unsaved-changes dialog). This matters: an
 * `onClick` that invalidates a query the current page's form is bound to
 * (e.g. via react-hook-form's `values` option) must not run while the user
 * still might cancel and keep editing.
 *
 * Forwards its ref to the underlying `<a>` — required for Radix `asChild`
 * consumers (e.g. the sidebar's collapsed-icon `Tooltip`) that need the real
 * DOM node to anchor their positioning.
 */
export const GuardedLink = forwardRef<HTMLAnchorElement, GuardedLinkProps>(
  ({ to, onClick, disabled, ...props }, ref) => {
    const { guardedNavigate } = useNavigationGuard();
    const navigate = useNavigate();

    const isPlainLeftClick = (event: MouseEvent<HTMLAnchorElement>) =>
      event.button === 0 &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.shiftKey &&
      !event.altKey;

    const onLinkClick = (event: MouseEvent<HTMLAnchorElement>) => {
      if (disabled) {
        event.preventDefault();

        return;
      }

      // Let the browser handle new-tab/new-window clicks (modifier keys,
      // middle-click) natively — only intercept a plain in-app navigation.
      if (!isPlainLeftClick(event)) return;

      event.preventDefault();
      guardedNavigate(() => {
        onClick?.(event);
        navigate(to);
      });
    };

    return <Link ref={ref} to={to} onClick={onLinkClick} {...props} />;
  },
);

GuardedLink.displayName = 'GuardedLink';
